class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    
    if (countContainerDesktop){
      countContainerDesktop.parentElement.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      // Always fetch fresh — never serve from cache. Cached no-results responses
      // would permanently hide filters that should reappear after the user
      // corrects an invalid price or clicks "Remove All".
      FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.parentElement.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // Find the filter element the user is currently interacting with (if any)
    const activeJsFilter = event ? event.target.closest('.js-filter') : null;

    // For each form container that holds filters, update each .js-filter element
    // one-to-one by data-index. Never remove filters from DOM so they persist
    // when Shopify returns fewer filters (e.g. no-results state).
    const formSelectors = ['#FacetFiltersForm', '#FacetFiltersFormMobile', '#FacetFiltersPillsForm'];

    formSelectors.forEach((formSelector) => {
      const liveForm = document.querySelector(formSelector);
      const parsedForm = parsedHTML.querySelector(formSelector);
      if (!liveForm || !parsedForm) return;

      // Get all filters from the parsed response for this specific form
      const parsedFilters = parsedForm.querySelectorAll('.js-filter');

      parsedFilters.forEach((parsedFilter) => {
        const idx = parsedFilter.dataset.index;
        const liveFilter = liveForm.querySelector(`.js-filter[data-index="${idx}"]`);

        if (!liveFilter) return; // Filter doesn't exist in live DOM — don't create it

        // If this is the actively interacted filter, use renderCounts to preserve
        // the open state and update only counts/checkboxes (not the price inputs)
        const isActive = activeJsFilter && activeJsFilter.dataset.index === idx
          && activeJsFilter.closest(formSelector);

        if (isActive) {
          FacetFiltersForm.renderCounts(parsedFilter, liveFilter);
        } else {
          liveFilter.innerHTML = parsedFilter.innerHTML;
        }
      });

      // After updating, remove any duplicate .js-filter elements that share
      // the same data-index (keep only the first occurrence of each index).
      // This is a safety net in case any render path creates duplicates.
      const seenIndexes = new Set();
      liveForm.querySelectorAll('.js-filter').forEach((el) => {
        if (seenIndexes.has(el.dataset.index)) {
          el.remove();
        } else {
          seenIndexes.add(el.dataset.index);
        }
      });

      // Sync checkbox checked property from the HTML attribute for every filter
      // in this form. Setting innerHTML (or replaceWith on a cloned node) sets
      // the defaultChecked attribute but browsers may not reflect it in the
      // live `checked` property when the element was previously interacted with.
      // Explicitly setting the property ensures :checked CSS and visual state match.
      // Also sync the `.active` class on the parent label to match checked state.
      //
      // Exception: for the actively interacted filter we preserve the live
      // `.checked` property as-is, because the user just toggled it and the
      // server HTML attribute may not yet reflect that change (mobile filters
      // don't go through renderCounts for their checkbox list, so the HTML
      // attribute on the input is still the pre-click value).
      liveForm.querySelectorAll('.js-filter input[type="checkbox"]').forEach((input) => {
        const parentFilter = input.closest('.js-filter');
        const isActiveFilter = activeJsFilter
          && parentFilter
          && parentFilter.dataset.index === activeJsFilter.dataset.index
          && activeJsFilter.closest(formSelector);

        // For the active filter keep whatever the user just set; for all others
        // trust the server-rendered HTML attribute.
        const shouldBeChecked = isActiveFilter
          ? input.checked
          : input.hasAttribute('checked');

        input.checked = shouldBeChecked;
        // Sync .active class on desktop facet-checkbox labels
        const label = input.closest('label.facet-checkbox, .swatch-input-wrapper.facet-checkbox');
        if (label) label.classList.toggle('active', shouldBeChecked);
      });
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      // Use replaceWith instead of outerHTML assignment to avoid stale node references
      targetElement.replaceWith(sourceElement.cloneNode(true));
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      targetElementAccessibility.replaceWith(sourceElementAccessibility.cloneNode(true));
    }
    
    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }
      // Use replaceWith to keep a live DOM reference and prevent duplicate nodes
      targetWrapElement.replaceWith(sourceWrapElement.cloneNode(true));
    }
    
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  async onSubmitHandler(event) {
    event.preventDefault();

    // Step 1 & 2: Synchronous validations — fast, no network cost.
    // Checks: min > collection max, min > entered max.
    const priceRangeElements = document.querySelectorAll('price-range');
    for (const priceRange of priceRangeElements) {
      if (!priceRange.validate()) {
        // Invalid price input — don't submit the form
        return;
      }
    }

    // Step 3: Async pre-flight — verify at least one product exists in the range
    // before submitting. Mirrors the existing validate() pattern exactly.
    for (const priceRange of priceRangeElements) {
      const hasProducts = await priceRange.checkProductsInPriceRange();
      if (hasProducts === false) {
        // Error already shown by checkProductsInPriceRange — block submission
        return;
      }
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm' || form.id === 'FacetFilterActiveForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange() {
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', minInput.dataset.originalMax || minInput.getAttribute('max'));
  }

  // Returns true if the current price inputs are valid for submission.
  // Removes any existing error message on valid state.
  validate() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    // The original collection max is stored on the input's initial max attribute
    // (set by Liquid as filter.range_max). Cache it on first access.
    if (!minInput.dataset.originalMax) {
      minInput.dataset.originalMax = minInput.getAttribute('max');
    }
    if (!maxInput.dataset.originalMax) {
      maxInput.dataset.originalMax = maxInput.getAttribute('max');
    }

    const collectionMax = parseFloat(minInput.dataset.originalMax);
    const enteredMin = minInput.value !== '' ? parseFloat(minInput.value) : null;
    const enteredMax = maxInput.value !== '' ? parseFloat(maxInput.value) : null;

    this.clearError();

    // Min exceeds collection's maximum available price
    if (enteredMin !== null && !isNaN(collectionMax) && enteredMin > collectionMax) {
      this.showError(
        `Minimum price cannot exceed the maximum available price of ${this.formatCurrency(collectionMax)}`
      );
      return false;
    }

    // Min exceeds entered max
    if (enteredMin !== null && enteredMax !== null && enteredMin > enteredMax) {
      this.showError('Minimum price cannot exceed maximum price');
      return false;
    }

    return true;
  }

  // Performs an async pre-flight fetch using the same section URL that renderPage
  // uses, so the result is guaranteed to match what the actual filter would show.
  // Returns true if products exist, false if the range is empty (and shows error),
  // or null if the check cannot be completed (fail-open so filter still works).
  async checkProductsInPriceRange() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    const enteredMin = minInput.value !== '' ? parseFloat(minInput.value) : null;
    const enteredMax = maxInput.value !== '' ? parseFloat(maxInput.value) : null;

    // Nothing entered — no range to validate
    if (enteredMin === null && enteredMax === null) return true;

    // Resolve the section ID the same way renderPage does
    const productGrid = document.getElementById('product-grid');
    if (!productGrid || !productGrid.dataset.id) return null;
    const sectionId = productGrid.dataset.id;

    // Build search params from all active filter forms, mirroring onSubmitForm logic,
    // but read the input values directly so we check what the user has entered now.
    const allForms = document.querySelectorAll('facet-filters-form form');
    const paramParts = [];
    allForms.forEach((form) => {
      const id = form.id;
      if (
        id === 'FacetSortForm' ||
        id === 'FacetFiltersForm' ||
        id === 'FacetSortDrawerForm' ||
        id === 'FacetFilterActiveForm' ||
        id === 'FacetFiltersFormMobile'
      ) {
        paramParts.push(new URLSearchParams(new FormData(form)).toString());
      }
    });
    const searchParams = paramParts.join('&');

    const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}&${searchParams}`;

    try {
      const response = await fetch(url, { headers: { 'Accept': 'text/html' } });
      if (!response.ok) return null;

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      // Try to read the product count from the section response.
      // The count element may contain something like "24 products" or just "0".
      const countEl = parsed.getElementById('ProductCount');
      if (!countEl) return null;

      // Extract leading integer from the count text (e.g. "0 products" → 0)
      const countText = countEl.textContent.trim();
      const countMatch = countText.match(/^\d+/);
      if (!countMatch) return null;

      const count = parseInt(countMatch[0], 10);
      if (count === 0) {
        this.showError('No products found within the selected price range.');
        return false;
      }

      return true;
    } catch {
      // Fail open — a network error should not block the filter
      return null;
    }
  }

  formatCurrency(amount) {
    const symbol = this.querySelector('.field-currency');
    const prefix = symbol ? symbol.textContent.trim() : '';
    return `${prefix}${amount.toFixed(2)}`;
  }

  showError(message) {
    let errorEl = this.querySelector('.price-range__error');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'price-range__error';
      errorEl.setAttribute('role', 'alert');
      errorEl.style.cssText = 'color:#c00;font-size:1.2rem;margin-top:6px;';
      this.appendChild(errorEl);
    }
    errorEl.textContent = message;
  }

  clearError() {
    const errorEl = this.querySelector('.price-range__error');
    if (errorEl) errorEl.remove();
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);